/**
 * Browser-side photo prep for the vision log path (POST /me/logs).
 *
 * Mirrors the mobile `downscaleForVision` intent: cap the longest edge so a
 * large camera photo doesn't blow past the API body limit (413) or inflate
 * vision token cost — pixtral downscales internally anyway. Returns the base64
 * the endpoint expects plus a data URL for the confirmation preview.
 */

/** Longest-edge cap in pixels — matches mobile MAX_IMAGE_EDGE. */
const MAX_IMAGE_EDGE = 1536;

/** MIME types the vision endpoint accepts (mirrors createLogSchema). */
export type VisionMediaType = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif';
const ACCEPTED: readonly string[] = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export interface VisionImage {
  /** Bare base64 (no data: prefix) for imageBase64. */
  base64: string;
  /** Actual MIME of the encoded bytes. */
  mediaType: VisionMediaType;
  /** Data URL for an <img> preview. */
  dataUrl: string;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = dataUrl;
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Reads a picked file, downscaling to {@link MAX_IMAGE_EDGE} when needed.
 * Re-encodes to JPEG only when it actually resizes; otherwise passes the
 * original bytes through untouched. Throws if the file isn't an accepted image.
 */
export async function fileToVisionImage(file: File): Promise<VisionImage> {
  if (!ACCEPTED.includes(file.type)) {
    throw new Error(`unsupported image type: ${file.type || 'unknown'}`);
  }
  const originalDataUrl = await readAsDataUrl(file);
  const img = await loadImage(originalDataUrl);

  const longest = Math.max(img.width, img.height);
  if (longest <= MAX_IMAGE_EDGE) {
    return {
      base64: originalDataUrl.split(',')[1] ?? '',
      mediaType: file.type as VisionMediaType,
      dataUrl: originalDataUrl,
    };
  }

  const scale = MAX_IMAGE_EDGE / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // ponytail: no 2d context (ancient browser) → upload original rather than fail.
    return {
      base64: originalDataUrl.split(',')[1] ?? '',
      mediaType: file.type as VisionMediaType,
      dataUrl: originalDataUrl,
    };
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
  return { base64: dataUrl.split(',')[1] ?? '', mediaType: 'image/jpeg', dataUrl };
}
